import type { ReactNode } from 'react';
import {
  CartaoIndicador,
  Selo,
  SeloStatusContrato,
  STATUS_CONTRATO,
  ValorFiscal,
  type StatusContrato,
  type TamanhoSelo,
} from '../ui';
import { motivoDeIndisponibilidade } from '../../servicos/painel';

/**
 * Peças que os dois painéis compartilham.
 *
 * Elas moram aqui, e não duplicadas em cada tela, porque as duas carregam regra
 * que não pode divergir entre uma e outra: o tratamento de status desconhecido e
 * a recusa em exibir `mesesAproveitamento` (D-008). Uma cópia é uma chance de
 * uma das telas voltar a mostrar `0`.
 *
 * Formatação de valor **não** está aqui: quem formata é `ValorFiscal` /
 * `ValorPercentual` da biblioteca de UI, um só implementador para o projeto
 * inteiro (RN-078).
 */

/** `SeloStatusContrato` só aceita os cinco estados de RN-018 a RN-030. */
function ehStatusConhecido(status: string): status is StatusContrato {
  return (STATUS_CONTRATO as readonly string[]).includes(status);
}

/**
 * Status de contrato, com o desconhecido aparecendo como veio.
 *
 * Inventar rótulo para um estado novo do backend esconde a divergência em vez de
 * mostrá-la — e quem descobre depois é o operador, no meio de um fechamento.
 */
export function SeloDeContrato(props: {
  readonly status: string | null;
  readonly tamanho?: TamanhoSelo;
  readonly textoSemContrato?: string;
}): JSX.Element {
  if (props.status === null) {
    return (
      <span className="text-conteudo-secundario">{props.textoSemContrato ?? 'sem contrato'}</span>
    );
  }

  return ehStatusConhecido(props.status) ? (
    <SeloStatusContrato status={props.status} tamanho={props.tamanho ?? 'pequeno'} />
  ) : (
    <Selo tamanho={props.tamanho ?? 'pequeno'}>{props.status}</Selo>
  );
}

/**
 * RN-175 / D-008 — meses aproveitados.
 *
 * A regra de contagem existe e está testada no domínio, mas nada ainda escreve a
 * confirmação do fornecedor que a alimenta. A API declara a métrica indisponível
 * em `metricasIndisponiveis`; a tela repete a declaração em vez de exibir o zero
 * que vem no campo. Zero aqui seria uma afirmação sobre a operação da empresa,
 * não sobre a falta do dado (dívida #46).
 *
 * Quando o fluxo de aprovação existir, a API para de declarar a métrica e este
 * cartão passa a mostrar o número sozinho — sem mudança de código.
 */
export function CartaoMesesAproveitados(props: {
  readonly meses: number;
  readonly metricasIndisponiveis: readonly string[];
}): JSX.Element {
  const indisponivel = motivoDeIndisponibilidade(
    props.metricasIndisponiveis,
    'mesesAproveitamento',
  );

  return (
    <CartaoIndicador
      rotulo="Meses aproveitados"
      valor={
        indisponivel === null ? (
          <ValorFiscal valor={props.meses} moeda={false} casas={0} tamanho="grande" />
        ) : (
          <ValorFiscal valor={null} tamanho="grande" />
        )
      }
      detalhe={
        indisponivel === null
          ? 'Competências com transferência de crédito confirmada pelo fornecedor.'
          : 'Indisponível: aguarda o fluxo de aprovação que registra a confirmação do fornecedor (D-008).'
      }
    />
  );
}

/**
 * O que a própria API declara que não consegue calcular.
 *
 * A tela repassa em vez de esconder — é o contrário do legado, que mandava `0` e
 * deixava a tela apresentá-lo como apuração.
 */
export function NotaDeMetricasIndisponiveis(props: {
  readonly metricasIndisponiveis: readonly string[];
}): JSX.Element | null {
  if (props.metricasIndisponiveis.length === 0) return null;

  return (
    <p className="text-xs text-conteudo-secundario">
      A API informa que estas métricas não são calculáveis hoje:{' '}
      {props.metricasIndisponiveis.join('; ')}.
    </p>
  );
}

/** Título de seção — mesma tipografia nos dois painéis. */
export function TituloDeSecao(props: {
  readonly id: string;
  readonly titulo: string;
  readonly descricao: string;
  /** À direita, na mesma linha — o filtro do ano, no Resumo. */
  readonly acao?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h2
          id={props.id}
          className="font-display text-lg font-semibold tracking-tight text-conteudo-primario"
        >
          {props.titulo}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-conteudo-secundario">{props.descricao}</p>
      </div>
      {props.acao !== undefined && <div className="shrink-0">{props.acao}</div>}
    </div>
  );
}
